import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { View, StyleSheet, ScrollView, Image, TouchableOpacity, Linking, Platform } from 'react-native'
import { Text, Spinner, Button, Toggle, Modal, OverflowMenu, Icon, CheckBox, Radio, MenuItem, Card, Input, useTheme } from '@ui-kitten/components'
import { format } from 'date-fns'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as ImagePicker from 'expo-image-picker'
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'
import { UserContext } from '../../global/UserProvider'
import { saveNewProduct, updateProduct, deleteProduct, uploadProductImage, deleteProductImage, storage } from '../../global/Firebase/firebase'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { usePostHog } from 'posthog-react-native'
import { IngredientsView } from '../../components/IngredientsView'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { useTranslation } from 'react-i18next'
import { useToast } from "react-native-toast-notifications"
import { useAtomValue } from 'jotai'
import { constantsAtom, organizationAtom, productCategoriesDictAtom, productUnitsDictAtom, productsDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom, visibleStoresForUserAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import Divider from '../../components/Divider'
import { generateBarcode, validateProduct } from '../../utils/productValidation'
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal'
import { InfoCard } from '../../components/InfoCard'
import { useForm, Controller } from 'react-hook-form'
import { useColors } from '../../utils/useColors'
import FormTextInput from '../../components/FormTextInput'
import FormToggle from '../../components/FormToggle'
import FormSelect from '../../components/FormSelect'
import FormRadio from '../../components/FormRadio'
import { PriceTypeEditor } from '../../components/PriceTypeEditor'
import { useProductDetailHeader } from './productDetailHeader'
import { stockThresholdInputValue } from '../../utils/productFormFields'
import { draftFormOverrides } from '../../global/Support/productDraftPrefill'
import { supportApi } from '../../global/Support/supportApi'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

const transformNumbersToString = (numbers) => {
  var newNumbers = {}
  for (var key in numbers) {
    // A map can carry null/undefined for a store (partial writes, deleted stores) —
    // .toString() on those white-screens the whole form (ATL-764 hardening).
    newNumbers[key] = numbers[key] != null ? numbers[key].toString() : ''
  }
  return newNumbers
}

const getDefaultActiveInStore = (visibleStores, storesDict) => {
  var newObj = {}
  for (var key in storesDict) {
    newObj[key] = visibleStores[key] ? true : false
  }
  return newObj
}

const getDefaultValuesForStores = (storesDict, value) => {
  var newObj = {}
  for (var key in storesDict) {
    newObj[key] = value
  }
  return newObj
}

const getFormValues = (productItem, {productCategoriesDict, suppliersDict, productUnitsDict, visibleStoresForUser, storesDict}) => {
  return {
    name: productItem?.name || '',
    purchasePrice: productItem?.purchasePrice ? productItem.purchasePrice.toString() : '',
    packagePrice: productItem?.packagePrice ? productItem.packagePrice.toString() : '0',
    activeInStore: productItem?.activeInStore || getDefaultActiveInStore(visibleStoresForUser, storesDict),
    salePrices: productItem?.salePrices ? transformNumbersToString(productItem.salePrices) : getDefaultValuesForStores(storesDict, ''),
    priceTypes: productItem?.priceTypes || {},
    priceTypeScopes: productItem?.priceTypeScopes || {},
    storeStock: productItem?.storeStock ? transformNumbersToString(productItem.storeStock) : getDefaultValuesForStores(storesDict, '0'),
    barcode: productItem?.barcode ? productItem.barcode.toString() : generateBarcode().toString(),
    barcodes: productItem?.barcodes || [],
    categoryID: productItem ? productCategoriesDict[productItem.categoryID] : null,
    supplierID: productItem ? suppliersDict[productItem.supplierID] : null,
    stockThreshold: stockThresholdInputValue(productItem?.stockThreshold),
    importance: productItem?.importance != null ? productItem.importance : 1,
    type: productItem?.type || false,
    productUnit: productItem ? productUnitsDict[productItem.productUnit] : Object.values(productUnitsDict)?.[0],
    isComposite: productItem?.isComposite || false,
    manufacturedBeforeSale: productItem?.manufacturedBeforeSale || false,
    ingredients: productItem?.ingredients || [],
    isHiddenInNewSale: productItem?.isHiddenInNewSale || false,
    isService: productItem?.isService || false,
    punchCardEnabled: productItem?.punchCardEnabled || false,
    description: productItem?.description || '',
    activeInEcommerce: productItem ? productItem.activeInEcommerce !== false : true,
    defaultQuantity: productItem?.defaultQuantity || '1',
    oldPrice: productItem?.oldPrice ? productItem.oldPrice.toString() : '',
    productImages: productItem?.productImages || []
  }
}

/**
 * ATL-1323 — the create form for a product a support agent drafted: the screen's own defaults for a
 * new product, with the draft's name, suggested sale price, unit (matched by name or short name),
 * category (matched by name) and
 * weight/service flags laid over them. Stock and cost price keep the manual-create defaults.
 */
const getDraftFormValues = (supportDraft, formDependencies) => {
  const base = getFormValues(null, formDependencies)
  return {
    ...base,
    ...draftFormOverrides(supportDraft.item, base.salePrices, formDependencies),
    // A weighted product needs a weighted (21…) barcode; validateProduct would otherwise swap the
    // shown one silently at save.
    barcode: generateBarcode(supportDraft.item.soldByWeight === true).toString(),
  }
}

export default function ProductDetailScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const { productItemProp, supportDraft } = route.params
  const [context, setContext] = useContext(UserContext)
  const productsDict = useAtomValue(productsDictAtom)
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const organization = useAtomValue(organizationAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const visibleStoresForUser = useAtomValue(visibleStoresForUserAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const db = context.db
  const toast = useToast()
  const postHog = usePostHog()

  const [productItem, setProductItem] = useState(productsDict[productItemProp?.id] || productItemProp)
  const [imageUploading, setImageUploading] = useState(false)
  const [imagePickerMenuVisible, setImagePickerVisible] = useState(false)
  const [modalImageURI, setModalImageURI] = useState(null)
  const [imageModalVisible, setImageModalVisible] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [compositeProductsUsingThis, setCompositeProductsUsingThis] = useState([])
  const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false)
  // ATL-1323: the support draft this create form was opened from, until the product is saved. Cleared
  // on the first successful save (and on "make a copy"), so exactly one product is reported against it.
  const [pendingSupportDraft, setPendingSupportDraft] = useState(
    !productItemProp && supportDraft?.actionId && supportDraft?.item ? supportDraft : null
  )

  const formDependencies = useMemo(() => ({
    productCategoriesDict,
    suppliersDict, 
    productUnitsDict,
    visibleStoresForUser,
    storesDict
  }), [productCategoriesDict, suppliersDict, productUnitsDict, visibleStoresForUser, storesDict])

  // Computed once, at mount — the only time useForm reads defaultValues. Evaluated inline, it re-ran on
  // every render, and each run scans the whole catalogue in generateBarcode (twice for a draft).
  const [initialFormValues] = useState(() => pendingSupportDraft
    ? getDraftFormValues(pendingSupportDraft, formDependencies)
    : getFormValues(productsDict[productItemProp?.id] || productItemProp, formDependencies))

  const { setValue, handleSubmit, control, watch, formState: { errors } } = useForm({
    defaultValues: initialFormValues
  })

  const watchIsService = watch('isService')
  const watchIsComposite = watch('isComposite')
  const watchIsHiddenInNewSale = watch('isHiddenInNewSale')
  const watchProductImages = watch('productImages')

  useEffect(() => {
    setProductItem(productsDict[productItemProp?.id] || productItemProp)
  }, [productItemProp])

  useEffect(() => {
    if (productItem) {
      const formValues = getFormValues(productItem, formDependencies)
      Object.entries(formValues).forEach(([key, value]) => {
        setValue(key, value)
      })

      // Reset success message and errors when switching products
      setSuccessMessageVisible(false)
      setSaveError('')

      // Check if this product is used as an ingredient in other products
      let productsUsingThis = []
      for (let [key, product] of Object.entries(productsDict)) {
        if (product.isComposite && product.ingredients && product.ingredients.length > 0) {
          if (product.ingredients.some(item => item.id === productItem.id)) {
            productsUsingThis.push(product)
          }
        }
      }
      setCompositeProductsUsingThis(productsUsingThis)

      // Log product detail data for E2E testing.
      // sanitizeForFirestore drops live Firestore DocumentReferences (cyclic on iOS) and is
      // cycle-safe — without it this JSON.stringify crashes the screen for composite products
      // whose ingredients carry a live ref (Sentry QARASAY-KASSA-26B, iOS-only). See ios cyclical-ref class.
      console.log('[PRODUCT_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: productItem.id,
        name: productItem.name,
        organizationId: organization?.id,
        barcode: productItem.barcode,
        barcodes: productItem.barcodes,
        purchasePrice: productItem.purchasePrice,
        packagePrice: productItem.packagePrice,
        salePrices: productItem.salePrices,
        categoryID: productItem.categoryID,
        categoryName: productCategoriesDict[productItem.categoryID]?.name,
        supplierID: productItem.supplierID,
        supplierName: suppliersDict[productItem.supplierID]?.name,
        productUnit: productItem.productUnit,
        productUnitName: productUnitsDict[productItem.productUnit]?.name,
        activeInStore: productItem.activeInStore,
        storeStock: productItem.storeStock,
        isService: productItem.isService,
        isComposite: productItem.isComposite,
        isHiddenInNewSale: productItem.isHiddenInNewSale,
        manufacturedBeforeSale: productItem.manufacturedBeforeSale,
        type: productItem.type,
        importance: productItem.importance,
        stockThreshold: productItem.stockThreshold,
        punchCardEnabled: productItem.punchCardEnabled,
        description: productItem.description,
        activeInEcommerce: productItem.activeInEcommerce,
        defaultQuantity: productItem.defaultQuantity,
        oldPrice: productItem.oldPrice,
        ingredients: productItem.ingredients,
        productImages: productItem.productImages?.map(img => img.fileName),
      })))

      downloadImages(productItem.productImages)
    }
  }, [productItem, formDependencies])

  // QARASAY-KASSA-26K: this MUST stay outside the `if (productItem)` effect above. When
  // makeACopy() nulls productItem the header has to lose its delete affordance — leaving the
  // previously installed headerRight closure mounted is what offered "delete" for a product
  // that does not exist yet, and deleteProduct(db, null, null, uid) then threw in
  // getFirestoreRef. See ./productDetailHeader for the full account.
  const renderHeaderDeleteButton = useCallback(() => (
    <HeaderIconButton
      testID='detail-delete-button'
      onPress={() => {
        setDeleteConfirmModalVisible(true)
      }}
      iconName={'trash-outline'}
    />
  ), [])

  useProductDetailHeader(navigation, {
    productItem,
    canDelete: Boolean(userRole?.permissions?.deleteProduct || context.user.role === 'admin'),
    newProductTitle: t('productsAndIngredients.new'),
    renderDeleteButton: renderHeaderDeleteButton,
  })

  const onSubmit = async (data) => {
    setIsLoading(true)
    setSaveError('')
    console.log('data', data)
    try {
      const productToValidate = {
        ...data,
        categoryID: data.categoryID?.id,
        supplierID: data.supplierID?.id,
        // Use firestoreId for productUnit since products.base_unit stores Firestore IDs
        productUnit: data.productUnit?.firestoreId || data.productUnit?.id,
        dailyStatID: getDailyStatID(null, new Date()),
      }

      const { errors, formattedProduct } = validateProduct(productToValidate, t, {
        isNew: !productItem,
        currentProductId: productItem?.id,
        lastChangeSource: !productItem && pendingSupportDraft ? 'supportDraft' : 'productDetail',
        organizationId: organization?.id,
        storeId: context.user.store.id,
        userId: context.user.uid
      })

      if (errors.length > 0) {
        throw new Error(errors.join('\n'))
      }

      if (productItem != null) {
        await updateProduct(db, productItem, formattedProduct, productsDict)
        sendFBAnalyticsEvent('update_product')
        postHog.capture('product_updated')
      } else {
        const newProductRef = await saveNewProduct(db, formattedProduct)
        sendFBAnalyticsEvent('new_product')
        postHog.capture('product_created')
        if (pendingSupportDraft) {
          const draft = pendingSupportDraft
          setPendingSupportDraft(null)
          postHog.capture('support_product_draft_saved', { actionId: draft.actionId, itemIndex: draft.item.index })
          reportSupportDraftSaved(draft, newProductRef.id)
        }
      }
      setIsLoading(false)
      setSuccessMessageVisible(true)
    } catch (error) {
      setIsLoading(false)
      setSaveError(saveFailureText(error, t, error.message))
      console.error(error)
      Sentry.captureException(error, { tags: { books_write_error: 'product-save' } })
    }
  }

  // Best-effort by design: the product exists either way. A report that does not land leaves the
  // chat card showing this item as unsaved, and the card flags a catalogue product with the same name
  // before the customer could save it twice.
  const reportSupportDraftSaved = (draft, firestoreId) => {
    supportApi.reportDraftItemApplied(draft.actionId, draft.item.index, firestoreId).catch((error) => {
      Sentry.captureException(error, {
        level: 'warning',
        tags: { feature: 'support-product-draft' },
        extra: { actionId: draft.actionId, itemIndex: draft.item.index, firestoreId },
      })
    })
  }

  const handleFormErrors = (errors) => {
    console.log('Form errors:', errors)
    setSaveError(t('notAllFieldsFilled'))
  }

  const makeACopy = async () => {
    // A copy is the customer's own product, not the agent's draft.
    setPendingSupportDraft(null)
    setValue('barcode', generateBarcode(watch('type')).toString())
    setSaveError('')
    setSuccessMessageVisible(false)
    setProductItem(null)
    setValue('storeStock', getDefaultValuesForStores(storesDict, '0'))
    setValue('name', t('productsAndIngredients.copy') + watch('name'))
    toast.show(t('productsAndIngredients.copyMadeToast'), {
      type: "custom",
      placement: "bottom",
      duration: 3000,
      offset: 30,
      animationType: "slide-in",
    })
  }

  const handleDelete = async () => {
    if (compositeProductsUsingThis.length > 0) {
      let error = new Error('PRODUCT_USED_AS_INGREDIENT')
      error.text = t('productsAndIngredients.productUsedAsIngredientError', { products: compositeProductsUsingThis.map(product => product.name).join(', ') })
      throw error
    }
    await deleteProduct(db, productItem, productItem, context.user.uid)
    navigation.goBack()
  }

  const downloadImages = async (productImages) => {
    var newProductImages = []
    // productImages can be undefined or a non-array (object) for legacy records
    // or via web SDK shape divergence; iterating a non-iterable throws (ATL-598).
    for (let value of (Array.isArray(productImages) ? productImages : [])) {
      try {
        const url = await storage.child('productImages/' + value.fileName).getDownloadURL()    
        newProductImages.push({...value, localUri: url})
      } catch(error) {
        // ATL-911 — KEEP the entry when its URL cannot be resolved.
        //
        // This used to skip the push, so a failed getDownloadURL() removed the image from the
        // form state entirely — and the next save wrote that shortened array back to Firestore,
        // destroying the `fileName` that is the ONLY durable identity the image has. A transient
        // Storage failure (offline till, expired token, a slow bucket) therefore became permanent
        // data loss, and it fired precisely when the network was already unreliable.
        //
        // Retaining `{fileName}` with no localUri means the image does not render this session and
        // resolves normally on the next one. Nothing is lost that a later attempt cannot recover.
        newProductImages.push({...value, localUri: null})
        Sentry.captureException(error)
        console.log(error)
      }
    }
    setValue('productImages', newProductImages)
  }

  let openImagePickerAsync = async (selectedIndex) => {
    var pickerResult = null
    if (selectedIndex == 0) {
      let permissionResult = await ImagePicker.requestCameraPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera roll is required!')
        return
      }
  
      pickerResult = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
      })
  
      if (pickerResult.canceled === true) {
        return
      }
    } else {
      // Use system photo picker without requesting broad media library permissions
      pickerResult = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 1,
      })
  
      if (pickerResult.canceled === true) {
        return
      }
    }
    
    setImageUploading(true)
    const fileName = watch('name') + ' ' + format(new Date(), 'd-M-y-H-m-s')

    const result = await manipulateAsync(
      pickerResult.assets[0].uri,
      [{ resize: { width: 1200 } }],
      { compress: 0.6, format: SaveFormat.JPEG }
    )

    try {
      if (Platform.OS === 'web') {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.onload = function() {
            resolve(xhr.response)
          };
          xhr.onerror = function(e) {
            console.log(e)
            reject(new TypeError('Network request failed'))
          };
          xhr.responseType = 'blob'
          xhr.open('GET', result.uri, true)
          xhr.send(null)
        })
        await uploadProductImage(blob, fileName)
      } else {
        await uploadProductImage(result.uri, fileName)
      }
  
      const newImages = [...watchProductImages, { fileName, localUri: result.uri }]
      setValue('productImages', newImages)
    } catch(error) {
      console.log(error)
      setSaveError(t('error'))
    }
    setImageUploading(false)
    setImagePickerVisible(false)
  }

  return (
    <KeyboardAwareScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='none' extraHeight={100}>
      <View style={{flexDirection: 'row', alignSelf: 'flex-end', marginRight: -20}}>
        <Button appearance='ghost' size='small' style={{marginRight: 0}} onPress={makeACopy} 
          accessoryLeft={(props) => (<Icon name='copy-outline' {...props} style={{...props.style, marginHorizontal: 0}} />)} >
          {(props) => (<Text {...props} style={{...props.style, marginHorizontal: 2}}>{t('productsAndIngredients.makeACopy')}</Text>)}
        </Button>
            
        {context.user.role === 'admin' && 
          <Button appearance='ghost' size='small' onPress={() => {
            Linking.openURL('https://atlas-app.notion.site/6d57af78274b4e0ea21752ebcf523e11')
            }} accessoryLeft={(props) => (<Icon name='question-mark-circle-outline' {...props} style={{...props.style, marginHorizontal: 0}} />)}>
            {(props) => (<Text {...props} style={{...props.style, marginHorizontal: 2}}>{t('productsAndIngredients.instructions')}</Text>)}
          </Button>
        }
      </View>
      {pendingSupportDraft && !productItem && (
        <InfoCard status='info' style={{marginTop: 15}}>
          <Text testID='product-detail-support-draft-banner'>{t('support.draftBanner')}</Text>
        </InfoCard>
      )}
      {compositeProductsUsingThis.length > 0 && (
        <InfoCard status='info' style={{marginTop: 15}}>
          <Text style={styles.infoText}>
            {t('productsAndIngredients.usedAsIngredient', { count: compositeProductsUsingThis.length })}
          </Text>
          <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
            {compositeProductsUsingThis.map((product, index) => (
              <TouchableOpacity 
                key={product.id} 
                style={{flexDirection: 'row', alignItems: 'center'}}
                onPress={() => navigation.navigate('ProductDetail', {productItemProp: product})}
              >
                <Text status='primary' style={styles.infoProduct}>
                  {product.name} 
                </Text>
                <Icon name='external-link-outline' height={15} width={15} fill={colors.primary} style={{marginLeft: 5}} />
              </TouchableOpacity>
            ))}
          </View>
        </InfoCard>
      )}
      <Text category='h5' style={styles.title}>
        {t('productsAndIngredients.general')}
      </Text>
      <FormTextInput
        control={control}
        name="name"
        rules={{ required: true }}
        style={styles.field}
        returnKeyType='done'
        placeholder={t('productsAndIngredients.namePlaceholder')}
        autoCapitalize='sentences'
        label={t('productsAndIngredients.name')}
        testID='product-detail-name'
      />
      {errors.name && <Text status='danger' style={styles.errorText}>{t('fieldIsRequiredError')}</Text>}

      <Text category='label' style={styles.label}>
        {t('productsAndIngredients.status')}
      </Text>
      {Object.values(visibleStoresForUser).map((item) => (
        <FormToggle
          key={item.firestoreId || item.id}
          control={control}
          name={`activeInStore.${item.firestoreId || item.id}`}
          style={styles.toggleField}
        >
          {t('productsAndIngredients.enabled') + (Object.values(visibleStoresForUser).length > 1 ? t('productsAndIngredients.forItem', {item: item.name}) : '')}
        </FormToggle>
      ))}

      <FormRadio
        control={control}
        name="isService"
        options={[
          { value: false, label: t('productsAndIngredients.product') },
          { value: true, label: t('productsAndIngredients.service') }
        ]}
        radioStyle={{marginBottom: 5}}
        style={{marginBottom: 10}}
        label={t('productsAndIngredients.productType')}
        testID='product-detail-is-service'
      />

      <FormToggle
        control={control}
        name="isHiddenInNewSale"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
        testID='product-detail-is-hidden'
      >
        {t('productsAndIngredients.hideInNewSaleScreen')}
      </FormToggle>

      {watchIsComposite && <Divider />}
      <FormToggle
        control={control}
        name="isComposite"
        style={styles.toggleField}
        testID='product-detail-is-composite'
      >
        {t('productsAndIngredients.composite')}
      </FormToggle>

      {watchIsComposite ? (
        <View>
          <FormRadio
            control={control}
            name="manufacturedBeforeSale"
            options={[
              { value: true, label: t('productsAndIngredients.manufacturedBeforeSale') },
              { value: false, label: t('productsAndIngredients.manufacturedDuringSale') }
            ]}
            radioStyle={{marginBottom: 5}}
            label={t('productsAndIngredients.manufactureType')}
          />
          <IngredientsView 
            parentProduct={productItem} 
            parentIngredients={watch('ingredients')} 
            setParentIngredients={(ingredients) => setValue('ingredients', ingredients)} 
            editable={true}
            parentPurchasePrice={watch('purchasePrice')} 
            setParentPurchasePrice={(price) => setValue('purchasePrice', price)} 
            navigation={navigation} 
          />
          <Divider />
        </View>
      ) : (
        <>
          <FormTextInput
            control={control}
            name="purchasePrice"
            label={t('productsAndIngredients.purchasePrice')}
            required={!watchIsComposite}
            placeholder="300"
            style={styles.field}
            isNumberInput={true}
            testID='product-detail-purchase-price'
          />
        </>
      )}

      <FormTextInput
        control={control}
        name="packagePrice"
        label={t('productsAndIngredients.additionalSpendingsToPurchasePrice')}
        placeholder="20"
        style={styles.field}
        isNumberInput={true}
        testID='product-detail-package-price'
      />

      {!watchIsHiddenInNewSale && Object.values(visibleStoresForUser).map((item) => (
        <FormTextInput
          key={(item.firestoreId || item.id)+'salePrice'}
          control={control}
          name={`salePrices.${item.firestoreId || item.id}`}
          label={t('productsAndIngredients.salePrice') + (Object.values(visibleStoresForUser).length > 1 ? item.name : '')}
          required={!watchIsHiddenInNewSale}
          placeholder="400"
          style={styles.field}
          isNumberInput={true}
        />
      ))}

      {!watchIsHiddenInNewSale && (
        <PriceTypeEditor
          product={{
            ...watch(),
            priceTypes: watch('priceTypes'),
            priceTypeScopes: watch('priceTypeScopes')
          }}
          onPriceChange={(updatedProduct) => {
            setValue('priceTypes', updatedProduct.priceTypes)
            setValue('priceTypeScopes', updatedProduct.priceTypeScopes)
          }}
        />
      )}

      <FormSelect
        control={control}
        name="categoryID"
        label={t('productsAndIngredients.category')}
        data={Object.values(productCategoriesDict)}
        required={true}
        style={styles.field}
        testID='product-detail-category'
        labelAccessory={() =>
          (userRole?.permissions?.['editProductCategories'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => navigation.navigate('ProductCategories')} style={{marginLeft: 5, padding: 3}}>
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )
        }
      />

      <FormSelect
        control={control}
        name="supplierID"
        label={t('productsAndIngredients.supplier')}
        data={Object.values(suppliersDict)}
        style={styles.field}
        testID='product-detail-supplier'
        labelAccessory={() =>
          (userRole?.permissions?.['editSuppliers'] || context.user.role === 'admin') && (
            <TouchableOpacity 
              onPress={() => navigation.navigate('UniversalList', {
                atom: suppliersDictAtom, 
                searchField: 'name', 
                detailScreen: 'SupplierDetail', 
                title: t('suppliers')
              })} 
              style={{marginLeft: 5, padding: 3}}
            >
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )
        }
      />

      <FormSelect
        control={control}
        name="productUnit"
        label={t('productsAndIngredients.productUnit')}
        data={Object.values(productUnitsDict)}
        required={true}
        style={styles.field}
        testID='product-detail-unit'
        labelAccessory={() =>
          (userRole?.permissions?.['editSettings'] || context.user.role === 'admin') && (
            <TouchableOpacity 
              onPress={() => navigation.navigate('UniversalList', {
                atom: productUnitsDictAtom, 
                searchField: 'name', 
                detailScreen: 'ProductUnitDetail', 
                title: t('productsAndIngredients.productUnits')
              })} 
              style={{marginLeft: 5, padding: 3}}
            >
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )
        }
      />

      <FormTextInput
        control={control}
        name="barcode"
        label={t('productsAndIngredients.barcode')}
        allowDecimals={false}
        style={styles.field}
        isNumberInput={true}
        testID='product-detail-barcode'
      />

      {watch('barcodes').map((barcode, index) => (
        <View key={index.toString()} style={{flexDirection: 'row', alignItems: 'center'}}>
          <FormTextInput
            control={control}
            name={`barcodes.${index}`}
            keyboardType='number-pad'
            style={{marginTop: 5, flex: 1}}
            returnKeyType='done'
            isNumberInput={true}
          />
          <TouchableOpacity 
            onPress={() => {
              const newBarcodes = [...watch('barcodes')]
              newBarcodes.splice(index, 1)
              setValue('barcodes', newBarcodes)
            }}
            style={{padding: 10, marginTop: 5}}
          >
            <Icon name='trash-2-outline' width={24} height={24} fill='#FF3D71'/>
          </TouchableOpacity>
        </View>
      ))}
      <Button
        onPress={() => {
          const newBarcodes = [...watch('barcodes'), '']
          setValue('barcodes', newBarcodes)
        }}
        style={{marginTop: 5}}
        appearance='ghost'
      >
        {t('productsAndIngredients.addAdditionalBarcode')}
      </Button>

      {!watchIsService && (
        <>
          <Text category='h5' style={styles.title}>
            {t('productsAndIngredients.forProductStocks')}
          </Text>
          {!productItem && Object.entries(watch('storeStock')).map(([key, value]) => (
            visibleStoresForUser?.[key] && (
              <View key={key}>
                <FormTextInput
                  control={control}
                  name={`storeStock.${key}`}
                  allowDecimals={true}
                  style={styles.field}
                  placeholder="1"
                  label={t('productsAndIngredients.inStockQuantity') + storesDict?.[key]?.name}
                  isNumberInput={true}
                />
              </View>
            )
          ))}

          <FormToggle
            control={control}
            name="type"
            style={styles.toggleField}
            textStyle={{flexShrink: 1}}
            label={t('productsAndIngredients.showStocksinFloat')}
            testID='product-detail-float-numbers'
          >
            {t('productsAndIngredients.floatNumbers')}
          </FormToggle>

          <FormToggle
            control={control}
            name="importance"
            style={styles.toggleField}
            onChange={(checked) => setValue('importance', checked ? 2 : 1)}
            checked={watch('importance') > 1}
            label={t('productsAndIngredients.showInImportantProducts')}
          >
            {t('productsAndIngredients.show')}
          </FormToggle>

          <FormTextInput
            control={control}
            name="stockThreshold"
            allowDecimals={true}
            style={styles.field}
            placeholder="4"
            label={t('productsAndIngredients.quantityToHighlightRed')}
            isNumberInput={true}
            testID='product-detail-stock-threshold'
          />
        </>
      )}

      {constants?.loyaltySettings?.punchCardEnabled && (
        <>
          <Text category='h5' style={styles.title}>
            {t('productsAndIngredients.forLoyaltyProgram')}
          </Text>
          <FormToggle
            control={control}
            name="punchCardEnabled"
            style={styles.toggleField}
            label={t('productsAndIngredients.punchCardParticipation')}
          >
            {t('productsAndIngredients.participates')}
          </FormToggle>
        </>
      )}

      {organization?.linkEnabled && (
        <>
          <Text category='h5' style={styles.title}>
            {t('productsAndIngredients.forEcommerce')}
          </Text>

          {!watchIsService && (
            <>
              <FormTextInput
                control={control}
                name="defaultQuantity"
                allowDecimals={true}
                style={styles.field}
                placeholder="0.2"
                label={t('productsAndIngredients.defaultQuantity')}
                isNumberInput={true}
              />
            </>
          )}

          <Text category='label' style={styles.label}>
            {t('productsAndIngredients.photos')}
          </Text>
          <ScrollView horizontal={true} style={styles.imagesContainer}>
            {imageUploading ? (
              <View style={styles.spinnerView}>
                <Spinner />
              </View>
            ) : (
              <OverflowMenu
                visible={imagePickerMenuVisible}
                placement='right'
                selectedIndex={null}
                backdropStyle={styles.backdrop}
                onSelect={(selectedIndex) => openImagePickerAsync(selectedIndex.row)}
                onBackdropPress={() => setImagePickerVisible(false)}
                anchor={() => (
                  <Button onPress={() => setImagePickerVisible(true)} style={styles.addMedia} status='basic' accessoryLeft={(style) => (
                    <Icon {...style} fill='#8f9bb3' name='plus-outline'/>
                  )} />
                )}
              >
                <MenuItem title={t('productsAndIngredients.takePhoto')} />
                <MenuItem title={t('productsAndIngredients.chooseFromGallery')} />
              </OverflowMenu>
            )}
            {watchProductImages.map((item, index) => (
              item.localUri != null ? (
                <View key={item+index}>  
                  <TouchableOpacity onPress={() => {
                    setModalImageURI(item.localUri)
                    setImageModalVisible(true)
                  }}> 
                    <Image
                      source={{ uri: item.localUri }}
                      style={styles.thumbnail}
                    />
                  </TouchableOpacity>
                  <Button appearance='ghost' status='danger' size='tiny' onPress={() => {
                    const newImages = [...watchProductImages]
                    newImages.splice(index, 1)
                    // Remove from the form immediately; the storage delete is
                    // best-effort and must never crash the screen (ATL-559).
                    deleteProductImage(item.fileName).catch((error) => {
                      Sentry.captureException(error)
                      console.log(error)
                    })
                    setValue('productImages', newImages)
                  }}>
                    {t('delete')}
                  </Button>
                </View>
              ) : (
                <View key={item+index} style={styles.spinnerView}>
                  <Spinner />
                </View>
              )
            ))}
          </ScrollView>

          <Modal style={styles.imageModal} visible={imageModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setImageModalVisible(false)}>
            {modalImageURI && (
              <TouchableOpacity onPress={() => setImageModalVisible(false)}>
                <Image
                  source={{ uri: modalImageURI }}
                  style={styles.fullImage}
                />
              </TouchableOpacity>
            )}
          </Modal>
        </>
      )}

      {successMessageVisible && (
        <>
          <Text status='success' style={styles.messageText} testID='product-detail-success-msg'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton} testID='product-detail-back-btn'>
            {t('back')}
          </Button>
        </>
      )}

      {saveError !== '' && (
        <Text style={styles.messageText} status='danger'>{saveError}</Text>
      )}

      {isLoading ? (
        <View style={styles.spinner}><Spinner /></View>
      ) : (
        !successMessageVisible && (
          <Button style={styles.submitButton} onPress={handleSubmit(onSubmit, handleFormErrors)} testID='product-detail-save-btn'>
            {t('save')}
          </Button>
        )
      )}

      {productItem && (
        <Text appearance='hint' style={{textAlign: 'center', marginTop: 10}}>
          {productItem.id}
        </Text>
      )}

      <View style={{height: 100}}></View>

      <DeleteConfirmationModal
        visible={deleteConfirmModalVisible}
        onCancel={() => setDeleteConfirmModalVisible(false)}
        onConfirm={handleDelete}
      />
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  }, 
  label: {
    marginTop: 15, 
    marginBottom: 5,
  },
  title: {
    marginTop: 15, 
    marginBottom: 5
  },
  field: {
    // marginBottom: 15
  },
  toggleField: {
    marginBottom: 5
  },
  imagesContainer: {

  },
  thumbnail: {
    width: 150,
    height: 150,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
    resizeMode: 'contain',
  },
  addMedia: {
    width: 150,
    height: 150,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
  },
  spinnerView: {
    width: 150,
    height: 150,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
  },
  fullImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  imageModal: {
    width: '90%',
    height: '90%',
  },
  submitButton: {
    marginTop: 15
  },
  messageText: {
    marginVertical: 8,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  infoText: {
    marginBottom: 5,
  },
  infoProduct: {
    marginLeft: 10,
  },
  errorText: {
    marginTop: 5,
    marginBottom: 15,
  }
})
